// Imported ledger amounts are exact decimal strings. Formatting must never round through
// Number or combine currencies; unknown amounts remain unknown in the screen and export.
export const hasReconciledReceivables = row => Boolean(row?.receivables && typeof row.receivables === 'object');

export function formatReceivableMoney(amount, currency) {
  if (typeof currency !== 'string' || !/^[A-Z]{3}$/.test(currency)) return 'Unknown currency';
  if (typeof amount !== 'string' || !/^-?(?:0|[1-9]\d*)(?:\.\d+)?$/.test(amount)) return `${currency} unknown`;
  const [whole, fraction] = amount.split('.');
  return `${currency} ${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}${fraction === undefined ? '' : `.${fraction}`}`;
}

const dateOnly = value => {
  if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return 'Unknown';
  const parsed = new Date(`${value}T00:00:00.000Z`);
  return Number.isFinite(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value ? value : 'Unknown';
};

export function receivablesAssessmentPresentation(row) {
  const assessment = row?.receivables?.assessment || row?.receivablesAssessment;
  const labels = {
    missing: 'Not assessed', needs_assessment: 'Assessment needed', stale: 'Refresh needed',
    needs_data: 'Needs data', insufficient_data: 'Needs data', needs_review: 'Needs review',
    current: 'Existing assessment', available: 'Existing assessment', in_progress: 'In progress', pending: 'Pending',
  };
  return {
    label: labels[assessment?.status] || 'Needs review',
    reason: assessment?.reason || 'Assessment sufficiency has not been verified for this import.',
    lastAssessedAt: assessment?.lastAssessedAt || null,
  };
}

export function receivablesBalances(row) {
  const entries = row?.receivables?.balancesByCurrency;
  return Array.isArray(entries) ? entries.filter(entry => entry && typeof entry === 'object') : [];
}

const sourceLabels = { account_snapshot: 'Company aging snapshot', invoice_detail: 'Invoice detail', unknown: 'Unknown source' };
export function receivablesSourceText(row) {
  const entries = receivablesBalances(row);
  return entries.length ? entries.map(entry => `${entry.currency || 'Unknown currency'}: ${sourceLabels[entry.sourceKind] || 'Unknown source'}${entry.sourceAsOfDate ? ` as of ${dateOnly(entry.sourceAsOfDate)}` : ''}`).join(' · ') : 'Unknown source';
}

export function receivablesLastReportedText(row) {
  return receivablesBalances(row).filter(entry => entry.sourceKind === 'account_snapshot' || entry.lastReportedAmount !== undefined).map(entry => `Last reported ${formatReceivableMoney(entry.lastReportedAmount, entry.currency)} as of ${dateOnly(entry.sourceKind === 'account_snapshot' ? entry.sourceAsOfDate : entry.reportAsOfDate)}`).join(' · ');
}

export function receivablesSourceEvidenceText(row) {
  return receivablesBalances(row).filter(entry => entry.snapshotSource && typeof entry.snapshotSource === 'object').map(entry => {
    const source = entry.snapshotSource;
    const parts = [source.sourceName ? `File: ${source.sourceName}` : 'Source file unavailable', source.sourceSheet ? `sheet: ${source.sourceSheet}` : '', Number.isSafeInteger(source.sourceRow) && source.sourceRow > 0 ? `row: ${source.sourceRow}` : '', typeof source.rowHash === 'string' && source.rowHash ? `row hash: ${source.rowHash}` : '', typeof source.importId === 'string' && source.importId ? `import: ${source.importId}` : ''].filter(Boolean);
    return `${entry.currency || 'Unknown currency'} — ${parts.join('; ')}`;
  }).join(' · ');
}

export function receivablesRecordCountsText(row) {
  const count = value => Number.isSafeInteger(value) && value >= 0 ? value : 'unknown';
  return receivablesBalances(row).map(entry => {
    const basis = entry.sourceKind === 'account_snapshot'
      ? `${count(entry.snapshotCount)} company aging snapshots; invoice detail not supplied`
      : `${count(entry.invoiceCount)} invoices${entry.corroboratingSnapshotCount > 0 ? `; ${count(entry.corroboratingSnapshotCount)} corroborating company aging snapshots` : ''}`;
    return `${entry.currency || 'Unknown currency'}: ${basis}, ${count(entry.unallocatedCount)} unallocated rows, ${count(entry.unknownRecordCount)} unknown balances`;
  }).join(' · ');
}

export function receivablesMoneyText(row, field = 'amountDue') {
  const entries = receivablesBalances(row);
  const amounts = entries.length ? entries.map(entry => {
    const datedFields = ['currentAmount', 'overdueAmount', 'aging30Amount', 'aging60Amount', 'aging90Amount', 'aging90PlusAmount'];
    const snapshot = entry.sourceKind === 'account_snapshot';
    const snapshotAtCutoff = snapshot && entry.snapshotCount > 0 && dateOnly(entry.sourceAsOfDate) !== 'Unknown' && entry.sourceAsOfDate === row?.receivables?.asOfDate;
    const staleSnapshot = snapshot && !snapshotAtCutoff;
    const noBaseline = entry.unknownBalanceCount > 0 || (entry.invoiceCount === 0 && !snapshotAtCutoff);
    const unknownRecords = entry.unknownRecordCount > 0;
    const undated = entry.unknownDueDateCount > 0 || (typeof entry.unknownDueDateAmount === 'string' && !/^-?0(?:\.0+)?$/.test(entry.unknownDueDateAmount));
    const uncertainDated = datedFields.includes(field) && (noBaseline || unknownRecords || undated || staleSnapshot);
    const amount = formatReceivableMoney(uncertainDated ? null : entry[field], entry.currency);
    if (field === 'amountDue' && staleSnapshot) return `${formatReceivableMoney(null, entry.currency)} (last reported ${formatReceivableMoney(entry.lastReportedAmount, entry.currency)} as of ${dateOnly(entry.sourceAsOfDate)}; snapshot does not establish this cutoff)`;
    const knownField = field === 'amountDue' ? 'knownAmountDue' : field === 'unallocatedAmount' ? 'knownUnallocatedAmount' : `known${field[0].toUpperCase()}${field.slice(1)}`;
    const known = entry[knownField] ?? (uncertainDated ? entry[field] : undefined);
    const reasons = [staleSnapshot ? 'snapshot does not establish this cutoff' : noBaseline ? 'opening balance unknown' : '', unknownRecords ? `${entry.unknownRecordCount} unknown records` : '', datedFields.includes(field) && undated ? 'due dates missing' : '', field === 'unallocatedAmount' && entry.unresolvedReversalCount > 0 ? `${entry.unresolvedReversalCount} unresolved reversals` : ''].filter(Boolean);
    if ((entry[field] === null || uncertainDated) && typeof known === 'string') return `${amount} (known portion: ${formatReceivableMoney(known, entry.currency)}${reasons.length ? `; ${reasons.join('; ')}` : ''})`;
    if (field === 'unknownDueDateAmount' && unknownRecords) return `${amount} (known portion; ${entry.unknownRecordCount} unknown balances)`;
    return amount;
  }).join(' · ') : 'Unknown';
  return [amounts, receivablesUnknownCurrencyText(row)].filter(Boolean).join(' · ');
}

export function receivablesUnknownCurrencyText(row) {
  const count = row?.receivables?.unknownCurrencyCount;
  return Number.isSafeInteger(count) && count > 0 ? `${count} records have unknown currency; balances are incomplete` : '';
}

export const receivablesAsOfText = row => dateOnly(row?.receivables?.asOfDate);
export const receivablesImportText = row => typeof row?.receivables?.importId === 'string' ? row.receivables.importId : 'Unavailable';

export function receivablesLegacyCell(row, field, agingDays = { age1: '30', age2: '60', age3: '90' }) {
  if (!hasReconciledReceivables(row)) return undefined;
  if (field === 'Total AR Balance') return receivablesMoneyText(row);
  if (field === 'Current' || field === 'Current AR Balance') return receivablesMoneyText(row, 'currentAmount');
  if (field === 'AR in arrear') return receivablesMoneyText(row, 'overdueAmount');
  if (field.startsWith('AR Balance Aging ')) {
    // A custom 15/30/45 selection changes the meaning of each range. Only the
    // explicitly reconciled 1–30 / 31–60 / 61–90 / >90 buckets are supported.
    if (String(agingDays.age1) !== '30' || String(agingDays.age2) !== '60' || String(agingDays.age3) !== '90') return 'Unavailable';
    const key = { '30': 'aging30Amount', '60': 'aging60Amount', '90': 'aging90Amount', '90+': 'aging90PlusAmount' }[field.replace('AR Balance Aging ', '')];
    return key ? receivablesMoneyText(row, key) : 'Unavailable';
  }
  return undefined;
}

export const receivablesExportHeaders = ['Reconciled AR by currency', 'AR as of', 'AR source basis', 'AR last reported snapshot', 'AR source evidence', 'AR record counts', 'AR aging 1–30 days', 'AR aging 31–60 days', 'AR aging 61–90 days', 'AR aging >90 days', 'AR due date unknown', 'AR unallocated payments', 'AR unresolved reversal amount', 'AR unknown currency warning', 'AR assessment state', 'AR assessment reason', 'AR import ID'];
export function receivablesExportValues(row) {
  if (!hasReconciledReceivables(row)) return receivablesExportHeaders.map(() => '');
  const assessment = receivablesAssessmentPresentation(row);
  return [receivablesMoneyText(row), receivablesAsOfText(row), receivablesSourceText(row), receivablesLastReportedText(row), receivablesSourceEvidenceText(row), receivablesRecordCountsText(row), ...['aging30Amount', 'aging60Amount', 'aging90Amount', 'aging90PlusAmount', 'unknownDueDateAmount', 'unallocatedAmount', 'unresolvedReversalAmount'].map(field => receivablesMoneyText(row, field)), receivablesUnknownCurrencyText(row), assessment.label, assessment.reason, receivablesImportText(row)];
}
